import React from 'react';
import { cn } from '@/shared/utils/cn';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type CardPadding = 'sm' | 'md' | 'lg';
type CardShadow = 'sm' | 'md' | 'none';

export interface CardProps {
  children: React.ReactNode;
  className?: string;
  padding?: CardPadding;
  shadow?: CardShadow;
  /** Render as a different HTML element (default: div) */
  as?: React.ElementType;
}

// ---------------------------------------------------------------------------
// Style maps
// ---------------------------------------------------------------------------
const paddingClasses: Record<CardPadding, string> = {
  sm: 'p-3',
  md: 'p-5',
  lg: 'p-7',
};

const shadowClasses: Record<CardShadow, string> = {
  none: '',
  sm: 'shadow-card',
  md: 'shadow-card-md',
};

// ---------------------------------------------------------------------------
// Card component
// ---------------------------------------------------------------------------
const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    {
      children,
      className,
      padding = 'md',
      shadow = 'sm',
      as: Tag = 'div',
      ...rest
    },
    ref,
  ) => {
    return (
      <Tag
        ref={ref}
        className={cn(
          'bg-white rounded-xl border border-slate-200',
          paddingClasses[padding],
          shadowClasses[shadow],
          className,
        )}
        {...rest}
      >
        {children}
      </Tag>
    );
  },
);

Card.displayName = 'Card';

export default Card;
